import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Brain, FileText, Warning, ArrowsClockwise } from "@phosphor-icons/react";

const DOMAIN_LABELS = {
  legal_entity: "⚖️ Légal", branding: "🎨 Branding", franchise: "🏛️ Franchise",
  students: "🥋 Élèves", coaches: "👨‍🏫 Coaches", events: "🎯 Événements",
  finances: "💰 Finances", debts: "💳 Dettes", monthly_expenses: "📊 Dépenses",
  sponsors: "🤝 Commanditaires", communications: "📧 Communications",
  documents: "📄 Documents", strategic_plans: "🗺️ Plans stratégiques",
};

const STATUS_COLORS = {
  confirmed: "bg-emerald-500/20 text-emerald-300 border-emerald-700/40",
  pending: "bg-amber-500/20 text-amber-300 border-amber-700/40",
  conflicting: "bg-red-500/20 text-red-300 border-red-700/40",
  archived: "bg-zinc-700/20 text-zinc-400 border-zinc-700/40",
  superseded: "bg-zinc-700/20 text-zinc-500 border-zinc-700/40",
};

export default function KaizenMemoryAdmin() {
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [entities, setEntities] = useState([]);
  const [facts, setFacts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [filterDomain, setFilterDomain] = useState("");
  const [scanning, setScanning] = useState(false);

  useEffect(() => { load(); }, [tab, filterDomain]);

  async function load() {
    try {
      if (tab === "overview") {
        const [s, g] = await Promise.all([
          api.get("/memory/review/summary"),
          api.get("/memory/review/gaps"),
        ]);
        setSummary(s.data);
        setGaps(g.data.gaps);
      } else if (tab === "entities") {
        const r = await api.get(`/memory/entities${filterDomain ? `?domain=${filterDomain}` : ""}`);
        setEntities(r.data.items);
      } else if (tab === "facts") {
        const r = await api.get(`/memory/facts?status=pending${filterDomain ? `&domain=${filterDomain}` : ""}`);
        setFacts(r.data.items);
      } else if (tab === "documents") {
        const r = await api.get(`/memory/documents${filterDomain ? `?domain_hint=${filterDomain}` : ""}`);
        setDocuments(r.data.items);
      } else if (tab === "decisions") {
        const r = await api.get("/memory/decisions");
        setDecisions(r.data.items);
      }
    } catch (e) { toast.error("Erreur : " + e.message); }
  }

  async function runScan() {
    setScanning(true);
    try {
      const r = await api.post("/memory/retroactive-scan");
      toast.success(`Scan terminé · ${r.data.ingested} nouveaux · ${r.data.duplicates} doublons · ${r.data.facts_total} faits`);
      load();
    } catch (e) { toast.error("Scan erreur : " + e.message); }
    finally { setScanning(false); }
  }

  async function confirmFact(factId, newStatus, promoteKey) {
    try {
      await api.post(`/memory/facts/${factId}/confirm`, {
        status: newStatus,
        promote_to_entity: promoteKey || null,
      });
      toast.success(promoteKey ? `Confirmé + promu à ${promoteKey}` : `Statut : ${newStatus}`);
      load();
    } catch (e) { toast.error("Erreur : " + e.message); }
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="kaizen-memory-admin">
      <header className="border-b border-zinc-800 pb-4 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-amber-400">
            <Brain size={22} weight="fill" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold">MÉMOIRE PERSISTANTE KAIZEN</span>
          </div>
          <h1 className="text-3xl font-bold text-zinc-100 mt-1">Source de vérité unique</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-3xl">
            5 couches : Entités canoniques · Documents · Faits extraits · Décisions · Versions.
            <span className="text-amber-300 ml-2 font-bold">ASK-LAST policy active.</span>
          </p>
        </div>
        <button onClick={runScan} disabled={scanning} data-testid="btn-scan-retro"
          className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50">
          <ArrowsClockwise size={14} className={scanning ? "animate-spin" : ""} weight="fill" />
          {scanning ? "Scan en cours…" : "Re-scan /app/memory"}
        </button>
      </header>

      <nav className="flex gap-2 border-b border-zinc-800 overflow-x-auto">
        {[
          { k: "overview", label: "📊 Vue d'ensemble" },
          { k: "entities", label: "🎯 Entités canoniques" },
          { k: "facts", label: "🔍 Faits à valider" },
          { k: "documents", label: "📄 Documents" },
          { k: "decisions", label: "⚖️ Décisions" },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`px-4 py-2 text-xs uppercase tracking-widest font-bold border-b-2 transition whitespace-nowrap ${
              tab === t.k ? "text-amber-300 border-amber-500" : "text-zinc-500 border-transparent hover:text-zinc-300"
            }`}>
            {t.label}
          </button>
        ))}
      </nav>

      {tab !== "overview" && tab !== "decisions" && (
        <div className="flex gap-2 items-center text-xs">
          <span className="text-zinc-500 uppercase tracking-widest font-bold">Filtre domaine :</span>
          <select value={filterDomain} onChange={e => setFilterDomain(e.target.value)} data-testid="filter-domain"
            className="bg-zinc-900 border border-zinc-800 rounded px-3 py-1.5 text-zinc-100">
            <option value="">Tous</option>
            {Object.entries(DOMAIN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      )}

      {tab === "overview" && summary && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Entités" value={summary.totals.entities} color="emerald" />
            <Stat label="Documents" value={summary.totals.documents} color="blue" />
            <Stat label="Faits total" value={summary.totals.facts} color="amber" />
            <Stat label="À valider" value={summary.totals.facts_pending} color="orange" />
            <Stat label="Décisions" value={summary.totals.decisions} color="purple" />
          </div>

          <section className="rounded-lg bg-zinc-900/60 border border-zinc-800 p-4">
            <h2 className="text-sm font-bold uppercase tracking-widest text-amber-300 mb-3">État par domaine</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {Object.entries(summary.domains).map(([d, s]) => (
                <div key={d} className="bg-zinc-950 rounded p-3 text-xs">
                  <div className="font-bold text-zinc-200 mb-1">{DOMAIN_LABELS[d] || d}</div>
                  <div className="flex gap-3 text-[10px] text-zinc-400">
                    <span>Ent: <strong className="text-emerald-400">{s.entities}</strong></span>
                    <span>Doc: <strong className="text-blue-400">{s.documents}</strong></span>
                    <span>Pend: <strong className="text-amber-400">{s.facts_pending}</strong></span>
                    <span>OK: <strong className="text-emerald-400">{s.facts_confirmed}</strong></span>
                    {s.facts_conflicting > 0 && <span>⚠️: <strong className="text-red-400">{s.facts_conflicting}</strong></span>}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-lg bg-red-950/20 border border-red-700/30 p-4">
            <h2 className="text-sm font-bold uppercase tracking-widest text-red-300 mb-3 flex items-center gap-2">
              <Warning size={14} weight="fill" /> Trous identifiés ({gaps.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {gaps.map((g, i) => (
                <div key={i} data-testid={`gap-${g.key}`} className="bg-zinc-950 rounded px-3 py-2 text-xs flex items-center justify-between">
                  <span><span className="text-zinc-500">{DOMAIN_LABELS[g.domain]}</span> · <code className="text-red-400">{g.key}</code></span>
                  <span className="text-[9px] uppercase tracking-widest text-red-400 font-bold">MANQUANT</span>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === "entities" && (
        <div className="space-y-2">
          <div className="text-xs text-zinc-500 mb-2">{entities.length} entités canoniques (source de vérité)</div>
          {entities.map(e => (
            <div key={e.id} data-testid={`entity-${e.id}`} className="rounded bg-zinc-900/60 border border-zinc-800 p-3">
              <div className="flex items-start gap-2 mb-1">
                <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-zinc-800 text-amber-300">{DOMAIN_LABELS[e.domain]}</span>
                <code className="text-xs text-zinc-300 font-bold">{e.key}</code>
                <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded border ${STATUS_COLORS[e.status] || ""}`}>{e.status}</span>
                <span className="ml-auto text-[10px] text-zinc-500">conf: {(e.confidence * 100).toFixed(0)}%</span>
              </div>
              <div className="text-sm text-zinc-100 mt-1 font-mono whitespace-pre-wrap break-all">
                {typeof e.value === "object" ? JSON.stringify(e.value, null, 2) : String(e.value)}
              </div>
              {e.notes && <div className="text-xs text-zinc-400 mt-1 italic">💡 {e.notes}</div>}
              {e.source && <div className="text-[10px] text-zinc-500 mt-1">Source : {e.source}</div>}
            </div>
          ))}
        </div>
      )}

      {tab === "facts" && (
        <div className="space-y-2">
          <div className="text-xs text-zinc-500 mb-2">{facts.length} faits en attente de validation (max 500 affichés)</div>
          {facts.slice(0, 100).map(f => (
            <div key={f.id} data-testid={`fact-${f.id}`} className="rounded bg-zinc-900/60 border border-zinc-800 p-3">
              <div className="flex items-start gap-2 mb-1">
                <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-zinc-800 text-amber-300">{DOMAIN_LABELS[f.domain]}</span>
                <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-blue-900/40 text-blue-300">{f.fact_type}</span>
                <span className="ml-auto text-[10px] text-zinc-500">conf: {(f.confidence * 100).toFixed(0)}%</span>
              </div>
              <div className="text-sm text-zinc-100 font-mono">{String(f.value)}</div>
              {f.raw_context && <div className="text-[10px] text-zinc-500 mt-1 italic line-clamp-2">…{f.raw_context}…</div>}
              <div className="flex gap-2 mt-2">
                <button onClick={() => confirmFact(f.id, "confirmed")} className="px-2 py-1 rounded bg-emerald-500/10 border border-emerald-700/40 text-emerald-300 text-[10px] font-bold uppercase">✓ Confirmer</button>
                <button onClick={() => confirmFact(f.id, "archived")} className="px-2 py-1 rounded bg-zinc-900 border border-zinc-700 text-zinc-400 text-[10px] font-bold uppercase">Archiver</button>
                <button onClick={() => confirmFact(f.id, "conflicting")} className="px-2 py-1 rounded bg-red-900/20 border border-red-700/40 text-red-400 text-[10px] font-bold uppercase">⚠ Conflit</button>
              </div>
            </div>
          ))}
          {facts.length > 100 && <div className="text-xs text-zinc-500 text-center py-2">Affichage limité à 100 faits</div>}
        </div>
      )}

      {tab === "documents" && (
        <div className="space-y-2">
          <div className="text-xs text-zinc-500 mb-2">{documents.length} documents indexés</div>
          {documents.map(d => (
            <div key={d.id} data-testid={`doc-${d.id}`} className="rounded bg-zinc-900/60 border border-zinc-800 p-3">
              <div className="flex items-start gap-2 mb-1">
                <FileText size={14} className="text-amber-400 mt-0.5" />
                <span className="font-bold text-zinc-100 text-sm flex-1">{d.name}</span>
                {d.domain_hint && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-zinc-800 text-amber-300">{DOMAIN_LABELS[d.domain_hint]}</span>}
                <span className="text-[10px] text-zinc-500">{d.facts_count} faits</span>
              </div>
              <div className="text-[10px] text-zinc-500">{d.created_at?.slice(0, 16)} · {(d.content_length || 0).toLocaleString()} car. · hash: {d.content_hash?.slice(0, 12)}…</div>
              <div className="text-xs text-zinc-400 mt-1 line-clamp-2">{d.summary}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "decisions" && (
        <div className="space-y-2">
          <div className="text-xs text-zinc-500 mb-2">{decisions.length} décisions versionnées</div>
          {decisions.length === 0 && <div className="text-center text-zinc-600 text-sm py-6">Aucune décision encore loggée</div>}
          {decisions.map(d => (
            <div key={d.id} className="rounded bg-zinc-900/60 border border-zinc-800 p-3">
              <div className="flex items-start gap-2">
                <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-zinc-800 text-amber-300">{DOMAIN_LABELS[d.domain]}</span>
                <span className="font-bold text-zinc-100 text-sm">{d.subject}</span>
                <span className="ml-auto text-[10px] text-zinc-500">{d.created_at?.slice(0, 16)}</span>
              </div>
              <div className="text-sm text-zinc-300 mt-1">{d.decision}</div>
              {d.justification && <div className="text-[10px] text-zinc-500 mt-1 italic">{d.justification}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  const cMap = {
    emerald: "text-emerald-300", blue: "text-blue-300", amber: "text-amber-300",
    orange: "text-orange-300", purple: "text-purple-300",
  };
  return (
    <div className="rounded-lg bg-zinc-900/60 border border-zinc-800 p-4 text-center">
      <div className={`text-3xl font-bold ${cMap[color] || "text-zinc-100"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 font-bold">{label}</div>
    </div>
  );
}
